import { Inject, Injectable } from "@angular/core";
import { Meta, Title } from "@angular/platform-browser";
import { Router } from "@angular/router";
import { select, Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { DocumentRef, ignoreNil, LocationService, removeTrailingSlash } from "@obg/ngx.global";
import { Log, LoggingFactory } from "@obg/ngx.global.evo";
import { RouteSelector } from "@obg/ngx.router";
import { flatMap, forEach, trimEnd, unescape } from "lodash-es";
import { combineLatest, Subscription } from "rxjs";
import { map, take, tap } from "rxjs/operators";

import { STARTUP_CONTEXT_TOKEN } from "../app-context/app-context.const";
import { StartupContext } from "../app-context/app-context.model";
import { AppContextSelector } from "../app-context/app-context.selector";
import { CoreConfig } from "../config/core-config";
import { CultureConfigService } from "../culture/culture.config";
import { MarketSelector } from "../market/market.selector";
import { Page, PageData } from "../page/page.model";
import { PageSelector2 } from "../page/page.selector2";
import { CoreState } from "../state";

import { DocumentMetaConfigService } from "./document-meta.config";
import { AlternativeCultureUrl, DocumentMetaData, MetaField, MetaFields, SeoContent } from "./document-meta.model";
import { getCulturedSlugs } from "./document-meta.util";

@Injectable({
	providedIn: "root",
})
export class DocumentMetaService {
	private document: Document;
	private canonicalLink: Element | undefined;
	private logger: Log;
	private hostDomain: string | undefined;

	constructor(
		documentRef: DocumentRef,
		loggingFactory: LoggingFactory,
		private coreConfig: CoreConfig,
		private meta: Meta,
		private title: Title,
		private router: Router,
		private locationService: LocationService,
		private documentMetaConfigService: DocumentMetaConfigService,
		private store: Store<CoreState>,
		private cultureConfig: CultureConfigService,
		private marketSelector: MarketSelector,
		private translateService: TranslateService,
		@Inject(STARTUP_CONTEXT_TOKEN) private startupContext: StartupContext
	) {
		this.document = documentRef.native;
		this.logger = loggingFactory.get("documentMetaService");
		this.hostDomain = coreConfig.frameAncestors;
	}

	mapPageToDocumentMetaData(pagePayload: PageData, routeHasCustomMetaData = false): DocumentMetaData {
		const page = pagePayload.page;

		if (!page) {
			this.logger.warn(
				"mapPageToDocumentMetaData",
				`Missing Page document for the URL ${this.document.location!.href}`,
				this.document.location
			);
		} else if (!routeHasCustomMetaData) {
			if (!page.metaDescription) {
				this.logger.debug(
					"mapPageToDocumentMetaData",
					`Missing "metaDescription" on Page document of ${this.document.location!.href}`,
					page
				);
			}
			if (!page.metaTitle) {
				this.logger.warn(
					"mapPageToDocumentMetaData",
					`Missing "metaTitle" on Page document of ${this.document.location!.href}`,
					page
				);
			}
		}

		const canonicalUrl = this.generateCanonicalUrl(page);

		return {
			canonicalUrl,
			alternateDeviceUrl: page.alternativeUrl || this.getAlternateDeviceUrl(canonicalUrl),
			metaDescription: page?.metaDescription === "" ? undefined : page?.metaDescription,
			metaTitle: page?.metaTitle === "" ? undefined : page?.metaTitle,
			seoContent: this.getSeoContent(page),
			alternativeUrls: this.generateAlternativeUrls(page),
		};
	}

	generateCanonicalUrl(page: Page): string {
		const origin = this.hostDomain || this.locationService.getOrigin();
		const href = origin.replace(/\/\/(m\.)/, "//www.");

		// protocol is not available in the document provided by Angular during SSR
		const isFullUrl =
			this.document.location.protocol.includes("http") || this.document.location.protocol.includes("https");
		const { pathname } = isFullUrl ? this.document.location : new URL(`${href}${this.document.location.href}`);
		return page?.canonicalUrl || trimEnd(`${href}${pathname}`, `/`);
	}

	generateAlternativeUrls(page: Page, seo = true): AlternativeCultureUrl[] {
		const alternatives: AlternativeCultureUrl[] = [];
		const parentRoutes$ = page.parentSegments.map((parentSegment) =>
			this.store.pipe(
				select(PageSelector2.get(parentSegment.key)),
				tap((parentPage) => {
					if (parentPage) {
						return;
					}

					this.logger.warn(
						"generateAlternativeUrls",
						`Parent page ${parentSegment.key} was not found for ${page.documentKey}`
					);
				})
			)
		);

		let parentPages: Page[] | undefined = [];

		combineLatest(parentRoutes$)
			.pipe(
				map((pages) => {
					if (pages.find((parentPage) => parentPage === undefined)) {
						return undefined;
					}

					return pages;
				}),
				tap<Page[] | undefined>((pages) => (parentPages = pages))
			)
			.subscribe()
			.unsubscribe();

		if (!parentPages) {
			return alternatives;
		}

		const culturedSlugs = getCulturedSlugs([...parentPages, page]);

		const externalAlternateDomains = this.documentMetaConfigService.getExternalHreflang();

		this.store
			.select(AppContextSelector.getCountryCode)
			.pipe(
				ignoreNil(),
				take(1),
				map((countryCode) => {
					for (const languageCode in culturedSlugs) {
						if (!culturedSlugs[languageCode]) {
							continue;
						}

						const culturedSlug = culturedSlugs[languageCode];
						if (!culturedSlug.culture) {
							continue;
						}

						const urlTree = this.router.createUrlTree(culturedSlug.urls);
						const currentUrl = this.locationService.getOrigin();
						const primaryDomain = this.getPrimaryDomain();
						const alternateDomain = this.getAlternateDomain(languageCode, seo);
						const blockDomain = this.getBlockDomain(languageCode);
						const isBlockDomain = !!blockDomain && currentUrl.includes(blockDomain);

						let alternateUrl: string;

						if (alternateDomain) {
							alternateUrl = isBlockDomain
								? currentUrl.replace(blockDomain!, alternateDomain)
								: currentUrl.replace(primaryDomain, alternateDomain);
						} else {
							alternateUrl = isBlockDomain ? currentUrl.replace(blockDomain!, primaryDomain) : currentUrl;
						}

						let url = removeTrailingSlash(
							new URL(`${languageCode}${urlTree.toString()}`, alternateUrl).toString()
						);

						if (
							urlTree.toString() === "/" &&
							externalAlternateDomains[languageCode] &&
							(externalAlternateDomains[languageCode] !== "https://www.betsson.es" ||
								countryCode.toLowerCase() === "es")
						) {
							url = externalAlternateDomains[languageCode];
						}

						alternatives.push({
							url,
							culture: culturedSlug.culture,
							languageCode,
						});
					}
				})
			)
			.subscribe(); // eslint-disable-line rxjs/no-ignored-subscription
		return alternatives;
	}

	updateDocumentMeta(documentMetaData: DocumentMetaData) {
		this.updateCanonicalLink(documentMetaData.canonicalUrl);
		this.updateAlternateUrlForDevice(documentMetaData.alternateDeviceUrl || "");

		if (typeof documentMetaData.metaDescription !== "undefined") {
			// todo: remove unescape once API starts decoding from their end
			this.updateMetaElement("description", this.decodeHtmlEntities(documentMetaData.metaDescription));
		}

		if (typeof documentMetaData.metaTitle !== "undefined") {
			this.updateMetaTitle(this.decodeHtmlEntities(documentMetaData.metaTitle));
		}

		if (this.documentMetaConfigService.getAlternativeUrlsEnabled() && documentMetaData.alternativeUrls) {
			this.updateAlternateUrls(documentMetaData.alternativeUrls);
		}
	}

	updateCanonicalLink(canonicalUrl: string, addLocationPrefix = false) {
		if (this.hostDomain || this.coreConfig.isCrawlableForCurrentMarket) {
			const origin = this.hostDomain || this.locationService.getOrigin();
			this.setCanonicalLink(addLocationPrefix ? `${origin}${canonicalUrl}` : canonicalUrl);
			return;
		}
		this.removeCanonicalLink();
	}

	updateMetaTitle(metaTitle: string) {
		this.title.setTitle(metaTitle);
	}

	updateMetaElement(name: string, content: string) {
		this.meta.updateTag({ name, content });
	}

	createCustomMetaTagsFromConfig() {
		const customMetaTags = this.documentMetaConfigService
			.getCustomMetaTags()
			.map((m) => ({ ...m, clearOnNavigation: false }) as MetaField);
		this.setMetaElements(customMetaTags);
	}

	setMetaElements(metaFields: MetaFields) {
		metaFields.map((metaField) => {
			if (metaField.title) {
				return this.title.setTitle(metaField.title as string);
			}

			if (metaField.charset) {
				return; // We don't allow for changing charset since it will then be unset by next navigation now that we removed persistOnNavigation.
			}

			let element;
			if (metaField.name) {
				element = this.meta.getTag(`name = "${metaField.name}"`);
			} else if (metaField.property) {
				element = this.meta.getTag(`property = "${metaField.property}"`);
			} else if (metaField.httpEquiv) {
				element = this.meta.getTag(`httpEquiv = "${metaField.httpEquiv}"`);
			}

			if (element) {
				this.meta.updateTag(metaField);
			} else {
				this.meta.addTag(metaField);
			}
		});
	}

	clearTagsOnNavigation() {
		this.updateMetaTitle("");
		this.updateMetaElement("description", "");
		this.meta.removeTag("clearOnNavigation");
	}

	updateAlternateUrls(alternateUrls: AlternativeCultureUrl[]): Subscription {
		for (const alternateElement of Array.from(this.document.querySelectorAll("link[rel=alternate][hreflang]"))) {
			alternateElement.remove();
		}

		const fragment = this.document.createDocumentFragment();
		const defaultCultureCode = this.cultureConfig.getDefaultCultureCode();
		const hreflangCultureCodes = this.cultureConfig.getHreflangCultureCodes();
		const customAlternativeUrls = this.documentMetaConfigService.getCustomAlternativeUrls();
		const hiddenHreflangCultureCodes = this.cultureConfig.getHiddenHreflangCultureCodes();
		const externalBrands = this.documentMetaConfigService.getExternalBrands();

		// language code is actually market code in this case
		return combineLatest([
			this.store.select(this.marketSelector.getAll()),
			this.store.select(RouteSelector.getCurrent).pipe(ignoreNil()),
		])
			.pipe(take(1))
			.subscribe(([markets, currentRoute]) => {
				const marketLanguageCodes = markets.map((market) => market.urlSlug ?? market.languageCode);
				const blockDomainLanguageCodes = this.coreConfig.blockDomainMarkets;
				const alternateDomainLanguageCodes = flatMap(
					this.coreConfig.alternateDomains,
					(domain) => domain.languageCodes
				);
				const languageCodes = [
					...marketLanguageCodes,
					...blockDomainLanguageCodes,
					...alternateDomainLanguageCodes,
				];

				const isSingleMarketBrand = marketLanguageCodes.length === 1;

				for (const brand of externalBrands) {
					const brandRoutes = this.startupContext.translatedExternalRoutes?.[brand.url];
					const routeSlug = brandRoutes?.[currentRoute.name]?.slug;

					if (routeSlug || routeSlug === "") {
						fragment.appendChild(
							this.createAlternateLink({
								href: `${brand.url}/${routeSlug}`,
								hreflang: brand.hreflang,
							})
						);
					}
				}

				for (const { url, languageCode, culture } of alternateUrls) {
					let href = url;
					if (!languageCodes.includes(languageCode) || hiddenHreflangCultureCodes.includes(languageCode)) {
						continue;
					}
					let hreflang = hreflangCultureCodes.includes(culture) ? culture : languageCode;
					const matchingAlternativeUrl = customAlternativeUrls?.find(
						(altUrl) => altUrl.matchMarketCode === languageCode
					);

					if (customAlternativeUrls && !matchingAlternativeUrl) {
						continue;
					}

					if (matchingAlternativeUrl?.hreflang) {
						hreflang = matchingAlternativeUrl.hreflang;
					}

					if (isSingleMarketBrand) {
						href = href.replace(`/${languageCode}`, "");
					}

					fragment.appendChild(this.createAlternateLink({ href, hreflang }));

					if (
						culture === defaultCultureCode &&
						!isSingleMarketBrand &&
						!this.cultureConfig.getXDefaultDisabled()
					) {
						fragment.appendChild(this.createAlternateLink({ href, hreflang: "x-default" }));
					}
				}

				this.document.head!.appendChild(fragment);
			});
	}

	updateAlternateUrlForDevice(href: string) {
		for (const alternateElement of Array.from(this.document.querySelectorAll("link[rel=alternate][media]"))) {
			alternateElement.remove();
		}

		if (!this.documentMetaConfigService.getAlternateSeoTagEnabled()) {
			return;
		}

		if (href) {
			this.document.head!.appendChild(
				this.createAlternateLink({ href, media: "only screen and (max-width: 640px)" })
			);
		}
	}

	getAlternateDeviceUrl(url: string): string {
		const deviceUrl = new URL(url);
		const deviceHost = deviceUrl.host.split(".");

		if (
			deviceHost.length >= 2 &&
			!deviceUrl.host.startsWith("m.") &&
			this.documentMetaConfigService.getAlternateSeoTagEnabled()
		) {
			const deviceDomain = `m.${deviceHost[deviceHost.length - 2]}.${deviceHost[deviceHost.length - 1]}`;
			deviceUrl.host = deviceDomain;
			return deviceUrl.href;
		}

		return "";
	}

	private getBlockDomain(languageCode: string): string | undefined {
		let prefixedKey = "general.block-domain";
		if (this.coreConfig?.blockDomainMarkets?.includes(languageCode)) {
			prefixedKey = `general.block-domain.${languageCode}`;
		}
		const translation = this.translateService.instant(prefixedKey);
		return translation !== prefixedKey ? translation : undefined;
	}

	private getPrimaryDomain(): string {
		return this.translateService.instant("general.primary-domain");
	}

	private createAlternateLink(options: { href: string; hreflang?: string; media?: string }): HTMLLinkElement {
		const element = this.document.createElement("link");
		element.setAttribute("rel", "alternate");
		forEach({ ...options, href: options.href.replace(/\/$/, "") }, (value, name) =>
			value ? element.setAttribute(name, value) : null
		);
		return element;
	}

	private setCanonicalLink(canonicalUrl: string) {
		if (canonicalUrl) {
			this.canonicalLink = this.getOrCreateCanonicalLink();
			this.canonicalLink.setAttribute("href", canonicalUrl);
		}
	}

	private removeCanonicalLink() {
		if (this.canonicalLink) {
			this.document.head!.removeChild(this.canonicalLink);
			this.canonicalLink = undefined;
		}
	}

	private getOrCreateCanonicalLink(): Element {
		let el = this.document.querySelector("link[rel=canonical]");

		if (!el) {
			el = this.document.createElement("link");
			el.setAttribute("rel", "canonical");
			this.document.head!.appendChild(el);
		}

		return el;
	}

	private getSeoContent(page: Page): SeoContent | undefined {
		const { h1Title, h2Title, h2Text, h3Title, h3Text, seoText } = page;

		return h1Title || h2Title || h2Text || h3Title || h3Text || seoText
			? {
					h1Title,
					h2Title,
					h2Text,
					h3Title,
					h3Text,
					seoText,
				}
			: undefined;
	}

	private getAlternateDomain(languageCode: string, seo: boolean): string | undefined {
		const blockDomain = this.coreConfig?.blockDomainMarkets?.includes(languageCode)
			? this.getBlockDomain(languageCode)
			: undefined;

		const seoAlternateDomain = this.coreConfig?.alternateDomains?.find((domain) =>
			domain?.languageCodes?.includes(languageCode)
		);

		if (!seoAlternateDomain || (!seo && !seoAlternateDomain?.generalUsage)) {
			return blockDomain;
		}

		return seoAlternateDomain.domain;
	}

	// Decode HTML to support emojis in HTML title
	private decodeHtmlEntities(text: string): string {
		const unescapedStr = unescape(text);

		return unescapedStr
			.replace(/&#(\d+);/g, (_, dec) => String.fromCodePoint(dec))
			.replace(/&#x([a-fA-F0-9]+);/g, (_, hex) => String.fromCodePoint(parseInt(hex, 16)));
	}
}
